/**
 * variant-gallery-filter.js
 * Filters the product media gallery to show only images that match the
 * selected variant's Type and Color options.
 *
 * How it works:
 * - Each product image has an alt text containing a recognised Type and/or Color
 *   keyword, e.g. "LastSwab Original Turquoise", "LastTissue Pack Green"
 * - Images without a recognisable type/color tag are shown for every variant
 * - Sets [hidden] on <slideshow-slide> elements AND their matching
 *   <button ref="thumbnails[]"> elements so both the main slideshow and the
 *   thumbnail strip are filtered consistently
 * - The filter scans ALL selected option values for a match so it works
 *   regardless of what the Shopify option is named ("Type", "Format", etc.)
 */

(function () {
  'use strict';

  const COLORS = ['Turquoise', 'Peach', 'Blue', 'Red', 'Black', 'Green'];
  const TYPES  = ['Original', 'Beauty', 'Baby', 'Pack', 'Box', 'PRO', 'Large'];

  /** Read ALL currently-selected option values from the variant picker */
  function getSelectedOptions() {
    var picker = document.querySelector('variant-picker');
    if (!picker) return {};
    var result = {};
    picker.querySelectorAll('input[type="radio"]:checked').forEach(function (input) {
      // Input name format: "OptionName-blockId-productId"
      var optionName = input.name.split('-')[0];
      result[optionName] = input.value;
    });
    return result;
  }

  /** Extract type and color classification from an alt text string */
  function classifyAlt(alt) {
    if (!alt) return { type: null, color: null };
    var type  = TYPES.find(function (t) { return alt.indexOf(t) !== -1; }) || null;
    var color = COLORS.find(function (c) { return alt.indexOf(c) !== -1; }) || null;
    return { type: type, color: color };
  }

  /** Apply the visibility filter to the active media gallery */
  function applyFilter() {
    var gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    var options = getSelectedOptions();

    // Scan all selected option values to find a matching Type / Color keyword.
    // Works regardless of what the Shopify option is named ("Type", "Format", etc.)
    var selectedType  = null;
    var selectedColor = null;
    Object.keys(options).forEach(function (key) {
      var val = options[key];
      if (!selectedType  && TYPES.indexOf(val)  !== -1) selectedType  = val;
      if (!selectedColor && COLORS.indexOf(val) !== -1) selectedColor = val;
    });

    // If no variant dimension is selected yet, reveal everything
    if (!selectedType && !selectedColor) {
      gallery.querySelectorAll('slideshow-slide').forEach(function (slide) {
        slide.removeAttribute('hidden');
      });
      gallery.querySelectorAll('[ref="thumbnails[]"]').forEach(function (thumb) {
        thumb.removeAttribute('hidden');
      });
      return;
    }

    // Collect slides and thumbnails — they share the same index order
    var slides     = Array.from(gallery.querySelectorAll('slideshow-slide'));
    var thumbnails = Array.from(gallery.querySelectorAll('[ref="thumbnails[]"]'));
    var visibleCount = 0;

    slides.forEach(function (slide, idx) {
      var img        = slide.querySelector('img[alt]');
      var classified = classifyAlt(img ? img.alt : '');
      var thumb      = thumbnails[idx] || null;

      // No type/color tags → generic image → always show
      if (!classified.type && !classified.color) {
        slide.removeAttribute('hidden');
        if (thumb) thumb.removeAttribute('hidden');
        visibleCount++;
        return;
      }

      var typeOK  = !classified.type  || classified.type  === selectedType;
      var colorOK = !classified.color || classified.color === selectedColor;

      if (typeOK && colorOK) {
        slide.removeAttribute('hidden');
        if (thumb) thumb.removeAttribute('hidden');
        visibleCount++;
      } else {
        slide.setAttribute('hidden', '');
        if (thumb) thumb.setAttribute('hidden', '');
      }
    });

    // Update the server-rendered total in the counter ("1 / 34" → "1 / 6")
    var counter = gallery.querySelector('.slideshow-controls__counter');
    if (counter) {
      var slash = counter.querySelector('.slash');
      if (slash && slash.nextSibling && slash.nextSibling.nodeType === Node.TEXT_NODE) {
        slash.nextSibling.textContent = visibleCount;
      }
      var currentSpan = counter.querySelector('[ref="current"]');
      if (currentSpan) currentSpan.textContent = '1';
    }

    // Tell the slideshow to scroll to the first visible slide
    var slideshow = gallery.querySelector('x-slideshow');
    if (slideshow && typeof slideshow.select === 'function') {
      slideshow.select(0, undefined, { animate: false });
    }
  }

  // Run after DOMContentLoaded — custom elements should be upgraded by this point
  // (all deferred scripts including Horizon's own JS execute before DOMContentLoaded)
  document.addEventListener('DOMContentLoaded', function () {
    requestAnimationFrame(applyFilter);
    updateTitleWithVariant();
  });

  // Re-run after every variant:update.
  // media-gallery replaces its DOM on variant change (line 48 of media-gallery.js:
  // `this.replaceWith(newMediaGallery)`), so by the time our document-level
  // handler fires the fresh gallery is already in the DOM.
  var _autoFixing = false;
  document.addEventListener('variant:update', function () {
    requestAnimationFrame(function () {
      applyFilter();
      if (!_autoFixing) {
        updateTitleWithVariant();
        autoFixUnavailableColor();
      }
    });
  });

  /**
   * If the currently selected colour is unavailable for the active type
   * (hidden by our display:none CSS), auto-click the first available colour
   * so the gallery never goes blank.
   */
  function autoFixUnavailableColor() {
    var picker = document.querySelector('variant-picker');
    if (!picker) return;

    var colorFieldset = picker.querySelector('.variant-option--swatches');
    if (!colorFieldset) return;

    var checked = colorFieldset.querySelector('input[type="radio"]:checked');
    if (!checked) return;

    if (checked.getAttribute('data-option-available') !== 'false') return;

    var firstAvailable = colorFieldset.querySelector('input[type="radio"][data-option-available="true"]');
    if (!firstAvailable) return;

    _autoFixing = true;
    firstAvailable.click();
    setTimeout(function () { _autoFixing = false; }, 300);
  }

  /**
   * Append the selected TYPE to the product title h1.
   * e.g. "LastSwab" → "LastSwab Original"
   * Stores the base title on first run so repeated calls don't stack.
   */
  function updateTitleWithVariant() {
    var h1 = document.querySelector('.product-information h1');
    if (!h1) return;

    // Store the original (base) title once
    if (!h1.dataset.baseTitle) {
      h1.dataset.baseTitle = h1.textContent.trim();
    }

    var options = getSelectedOptions();
    var selectedType = null;
    Object.keys(options).forEach(function (key) {
      var val = options[key];
      if (!selectedType && TYPES.indexOf(val) !== -1) selectedType = val;
    });

    if (selectedType) {
      h1.innerHTML = h1.dataset.baseTitle + ' <span class="vgf-type-name">' + selectedType + '</span>';
    } else {
      h1.textContent = h1.dataset.baseTitle;
    }
  }


})();
